import React, { useState, useEffect, useRef } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { loadModules } from 'esri-loader';
import { useAuth } from '../../../../AuthProvider';
import { addUnavailableNotice, applyDiscernShading, removeRetiredLayers } from '../../../../utils/discernWebmapShading';
import BlockNameHeading from '../../../CustomVestmapViewer/components/BlockNameHeading';
import GenericBlock from '../../../CustomVestmapViewer/components/GenericBlock';
import marker from '../../../CustomVestmapViewer/assets/marker center.png';

function MedianHouseholdIncome({ blockValue }) {
  if (!blockValue) return null;

  return (
    <Box
      sx={{
        flexBasis: '100%',
        width: '100%',
        gap: '1.5rem',
        paddingY: { xs: '0', sm: '1rem' },
        paddingX: { xs: '0', sm: '1.75rem' },
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <BlockNameHeading title='Median Household Income' />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
          width: '100%',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <GenericBlock
            sectionBlock={{
              title: 'Block',
              value: { block: blockValue?.block },
              outfields: ['block'],
              prefix: '$',
            }}
            tooltipTitle='Block'
            hideTooltips
          />
          <GenericBlock
            sectionBlock={{
              title: 'Tract',
              value: { tract: blockValue?.tract },
              outfields: ['tract'],
              prefix: '$',
            }}
            tooltipTitle='Tract'
            hideTooltips
          />
          <GenericBlock
            sectionBlock={{
              title: 'Zip',
              value: { zip: blockValue?.zip },
              outfields: ['zip'],
              prefix: '$',
            }}
            tooltipTitle='Zip'
            hideTooltips
          />
        </Box>
        <Box
          sx={{
            display: 'flex',
            flexDirection: { xs: 'column', sm: 'row' },
            gap: '1rem',
            width: '100%',
          }}
        >
          <GenericBlock
            sectionBlock={{
              title: 'County',
              value: { county: blockValue?.county },
              outfields: ['county'],
              prefix: '$',
            }}
            tooltipTitle='County'
            hideTooltips
          />
          <GenericBlock
            sectionBlock={{
              title: 'State',
              value: { state: blockValue?.state },
              prefix: '$',
              outfields: ['state'],
            }}
            tooltipTitle='State'
            hideTooltips
          />
          <GenericBlock
            sectionBlock={{
              title: 'National',
              value: { national: blockValue?.national },
              prefix: '$',
              outfields: ['national'],
            }}
            tooltipTitle='National'
            hideTooltips
          />
        </Box>
      </Box>
    </Box>
  );
}

function MedianHomeValue({ blockValue }) {
  if (!blockValue) return null;

  return (
    <Box
      sx={{
        flexBasis: '100%',
        width: '100%',
        gap: '1.5rem',
        paddingY: { xs: '0', sm: '1rem' },
        paddingX: { xs: '0', sm: '1.75rem' },
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <BlockNameHeading title='Median Home Value' />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '1rem',
          width: '100%',
        }}
      >
        <GenericBlock
          sectionBlock={{
            title: 'Block',
            value: { block: blockValue?.block },
            outfields: ['block'],
            prefix: '$',
          }}
          tooltipTitle='MHVB'
          hideTooltips
        />
        <GenericBlock
          sectionBlock={{
            title: 'Tract',
            value: { tract: blockValue?.tract },
            outfields: ['tract'],
            prefix: '$',
          }}
          tooltipTitle='MHVT'
          hideTooltips
        />
        <GenericBlock
          sectionBlock={{
            title: 'Zip',
            value: { zip: blockValue?.zip },
            outfields: ['zip'],
            prefix: '$',
          }}
          tooltipTitle='MHVZ'
          hideTooltips
        />
      </Box>
    </Box>
  );
}

function AnnualForecastedMedianIncomeGrowth({ blockValue }) {
  if (!blockValue) return null;

  return (
    <Box
      sx={{
        flexBasis: '100%',
        width: '100%',
        paddingY: '1rem',
        gap: '1.5rem',
        paddingX: { xs: '0', sm: '1.75rem' },
        borderRadius: '1rem',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
      }}
    >
      <BlockNameHeading title='Annual Forecasted Median Income Growth' />
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: '1rem',
          width: '100%',
        }}
      >
        <GenericBlock
          sectionBlock={{
            title: 'Tract',
            value: { tract: blockValue?.tract },
            outfields: ['tract'],
          }}
          tooltipTitle='Tract'
          hideTooltips
        />
        <GenericBlock
          sectionBlock={{
            title: 'Zip',
            value: { zip: blockValue?.zip },
            outfields: ['zip'],
          }}
          tooltipTitle='Zip'
          hideTooltips
        />
      </Box>
    </Box>
  );
}

function IncomeMap({ webmapID, token, addressInfo }) {
  const { getAccessToken } = useAuth();
  const mapRef = useRef(null);
  const [isMapLoaded, setIsMapLoaded] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const viewRef = useRef(null);

  useEffect(() => {
    if (!webmapID || !token || !addressInfo) return;

    let view;
    loadModules([
      'esri/Map',
      'esri/views/MapView',
      'esri/WebMap',
      'esri/geometry/Extent',
      'esri/Graphic',
      'esri/widgets/Legend',
      'esri/config',
      'esri/layers/FeatureLayer',
    ]).then(
      ([Map, MapView, WebMap, Extent, Graphic, Legend, esriConfig, FeatureLayer]) => {
        if (!webmapID || !token) return;

        esriConfig.apiKey = token;

        const extent = new Extent({
          xmin: addressInfo.address_xmin,
          ymin: addressInfo.address_ymin,
          xmax: addressInfo.address_xmax,
          ymax: addressInfo.address_ymax,
          spatialReference: {
            wkid: addressInfo.spatialReference?.wkid || 4326,
          },
        });

        const webmap = new WebMap({
          portalItem: {
            id: webmapID,
          },
        });

        // Redraw the shaded layer from 2026 GeoEnrichment values (flagged; any
        // failure or signed-out view keeps the authored 2024 layer).
        webmap.when(() => {
          removeRetiredLayers(webmap);
          applyDiscernShading({
            webmap,
            addressInfo,
            getAccessToken,
            deps: { FeatureLayer, Graphic },
            isStale: () => view?.destroyed === true || webmap?.destroyed === true,
            onUnavailable: () => addUnavailableNotice(view),
          });
        });

        view = new MapView({
          container: mapRef.current,
          map: webmap,
          extent: extent,
          zoom: 14,
          navigation: {
            mouseWheelZoomEnabled: false,
          },
        });

        viewRef.current = view;

        view
          .when(() => {
            setIsMapLoaded(true);
            setErrorMessage(null);

            view.ui.add(
              new Legend({
                view: view,
              }),
              'top-right',
            );

            if (
              addressInfo.address_longitude &&
              addressInfo.address_latitude
            ) {
              const customSymbol = {
                type: 'picture-marker',
                url: marker,
                width: '50px',
                height: '100px',
              };

              view.graphics.add(
                new Graphic({
                  geometry: {
                    type: 'point',
                    longitude: addressInfo.address_longitude,
                    latitude: addressInfo.address_latitude,
                  },
                  symbol: customSymbol,
                }),
              );
            }
          })
          .catch(err => {
            console.error('Error loading income map:', err);
            setIsMapLoaded(false);
            setErrorMessage(err.message || 'Failed to load map');
          });
      },
    );

    return () => {
      if (view) {
        view.destroy();
      }
    };
  }, [webmapID, token, addressInfo]);

  return (
    <Box
      sx={{
        width: '100%',
        height: {
          sm: '30rem',
          xs: '20rem',
        },
        position: 'relative',
      }}
    >
      {!isMapLoaded && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: 'rgba(0, 0, 0, 0.1)',
          }}
        >
          {errorMessage ? (
            <Typography
              variant='body2'
              sx={{ color: '#b00020', textAlign: 'center', p: 2 }}
            >
              {errorMessage}
            </Typography>
          ) : (
            <CircularProgress sx={{ color: '#4FA490' }} />
          )}
        </Box>
      )}
      <Box
        ref={mapRef}
        sx={{
          width: '100%',
          height: '100%',
          visibility: isMapLoaded ? 'visible' : 'hidden',
        }}
      />
    </Box>
  );
}

const IncomeCard = ({ type, addressInfo, incomeData, webmapID, arcgisToken }) => {
  if (type === 'map') {
    if (!webmapID || !arcgisToken || !addressInfo) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view income map
          </Typography>
        </Box>
      );
    }

    return <IncomeMap webmapID={webmapID} token={arcgisToken} addressInfo={addressInfo} />;
  }

  if (type === 'data') {
    if (!incomeData || Object.keys(incomeData).length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            Search for an address to view income data
          </Typography>
        </Box>
      );
    }

    const blocks = [];

    if (incomeData.median_household_income) {
      blocks.push(
        <MedianHouseholdIncome
          key='median_household_income'
          blockValue={incomeData.median_household_income}
        />,
      );
    }

    if (incomeData.median_home_value) {
      blocks.push(
        <MedianHomeValue
          key='median_home_value'
          blockValue={incomeData.median_home_value}
        />,
      );
    }

    if (incomeData.annual_forecasted_median_income_growth) {
      blocks.push(
        <AnnualForecastedMedianIncomeGrowth
          key='annual_forecasted_median_income_growth'
          blockValue={incomeData.annual_forecasted_median_income_growth}
        />,
      );
    }

    if (blocks.length === 0) {
      return (
        <Box
          sx={{
            height: '256px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: '#F3F4F6',
            borderRadius: '0.5rem',
            border: '2px dashed #D1D5DB',
          }}
        >
          <Typography sx={{ fontSize: '0.875rem', color: '#6B7280' }}>
            No income data available
          </Typography>
        </Box>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: '1rem',
        }}
      >
        {blocks.map(block => block)}
      </Box>
    );
  }

  return null;
};

export default IncomeCard;

